import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { 
  GraduationCap, BookOpen, Clock, Trophy, 
  PlayCircle, CheckCircle, Lock, ChevronRight,
  Target, Award, TrendingUp, Settings, Play, BarChart3,
  Flame, Star, Zap, ArrowRight, Sparkles, ClipboardList
} from 'lucide-react';

const API = API_BASE;

export default function TrainingDashboard() {
  const { user, canAccessTraining } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setError(null);
      const [modulesRes, progressRes] = await Promise.all([
        axios.get(`${API}/training/modules`),
        axios.get(`${API}/training/my-progress`)
      ]);
      setModules(modulesRes.data || []);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error fetching training data:', error);
      setError(error.response?.data?.detail || error.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccessTraining()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const lang = user?.training_language || 'en';
  const t = (en, es) => lang === 'es' ? es : en;

  const getModuleStatus = (module) => {
    const modProgress = module.user_progress || {};
    if (modProgress.completion_percentage >= 100) return 'completed';
    if (modProgress.started_at) return 'in_progress';
    return 'locked';
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  // Find current module for trainee (based on lessons, not quizzes)
  const getCurrentModule = () => {
    return modules.find(m => {
      const mp = m.user_progress || {};
      return mp.completion_percentage < 100;
    }) || modules[0];
  };

  // Calculate stats
  const completedModules = modules.filter(m => getModuleStatus(m) === 'completed').length;
  const totalLessonsCompleted = modules.reduce((acc, m) => {
    const mp = m.user_progress || {};
    return acc + (mp.lessons_completed || 0);
  }, 0);
  const totalLessons = modules.reduce((acc, m) => acc + (m.lessons_count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary/20 rounded-full"></div>
            <div className="absolute top-0 left-0 w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-muted-foreground animate-pulse">
            {t('Loading your progress...', 'Cargando tu progreso...')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
          <span className="text-4xl">⚠️</span>
        </div>
        <h2 className="text-2xl font-bold text-red-600">
          {t('Error Loading Academy', 'Error al cargar la Academia')}
        </h2>
        <p className="text-muted-foreground text-center max-w-md">{error}</p>
        <Button onClick={fetchData} size="lg" className="mt-4">
          {t('Try Again', 'Reintentar')}
        </Button>
      </div>
    );
  }

  if (modules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6">
        <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center">
          <GraduationCap className="h-12 w-12 text-muted-foreground" />
        </div>
        <h2 className="text-2xl font-bold">
          {t('No modules available', 'No hay módulos disponibles')}
        </h2>
        <p className="text-muted-foreground text-center max-w-md">
          {t('Training content is not configured yet.', 'El contenido de entrenamiento no está configurado.')}
        </p>
        {user?.role === 'admin' && (
          <Button onClick={() => navigate('/training/admin/editor')} size="lg" className="mt-4">
            <Settings className="h-4 w-4 mr-2" />
            {t('Configure Content', 'Configurar Contenido')}
          </Button>
        )}
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';
  const currentModule = getCurrentModule();

  return (
    <div className="min-h-screen" data-testid="training-dashboard">
      {/* Hero Section with Gradient */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0aDR2NGgtNHpNNDAgMzBoNHY0aC00ek0yOCAzNGg0djRoLTR6TTMyIDMwaDR2NGgtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50"></div>
        
        <div className="relative px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                    <GraduationCap className="h-6 w-6" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold">
                      {t('Training Academy', 'Academia de Capacitación')}
                    </h1>
                    <p className="text-white/60 text-sm">
                      {t('Welcome back', 'Bienvenido de nuevo')}, {user?.full_name?.split(' ')[0]}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/training/admin/assessments')}
                      className="border-white/20 bg-white/5 hover:bg-white/10 text-white"
                      data-testid="admin-assessments-btn"
                    >
                      <ClipboardList className="h-4 w-4 mr-2" />
                      {t('Assessments', 'Evaluaciones')}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/training/admin/editor')}
                      className="border-white/20 bg-white/5 hover:bg-white/10 text-white"
                      data-testid="admin-edit-content-btn"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      {t('Edit', 'Editar')}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/training/admin/dashboard')}
                      className="border-white/20 bg-white/5 hover:bg-white/10 text-white"
                      data-testid="admin-dashboard-btn"
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      {t('Reports', 'Reportes')}
                    </Button>
                  </>
                )}
                {progress?.certification && (
                  <Badge className="text-sm px-4 py-2 bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                    <Award className="h-4 w-4 mr-2" />
                    {t('Certified', 'Certificado')}
                  </Badge>
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Overall Progress */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Target className="h-5 w-5 text-blue-400" />
                  </div>
                  <span className="text-white/60 text-sm">
                    {t('Progress', 'Progreso')}
                  </span>
                </div>
                <p className="text-3xl font-bold">
                  {(progress?.overall_progress || 0).toFixed(0)}%
                </p>
                <Progress 
                  value={progress?.overall_progress || 0} 
                  className="mt-2 h-1.5 bg-white/10"
                />
              </div>

              {/* Modules Completed */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-emerald-400" />
                  </div>
                  <span className="text-white/60 text-sm">
                    {t('Modules', 'Módulos')}
                  </span>
                </div>
                <p className="text-3xl font-bold">
                  {completedModules}<span className="text-lg text-white/40">/{modules.length}</span>
                </p>
                <p className="text-xs text-white/40 mt-1">
                  {t('completed', 'completados')}
                </p>
              </div>

              {/* Time Invested */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-amber-400" />
                  </div>
                  <span className="text-white/60 text-sm">
                    {t('Time', 'Tiempo')}
                  </span>
                </div>
                <p className="text-3xl font-bold">
                  {formatTime(progress?.total_time_spent_minutes || 0)}
                </p>
                <p className="text-xs text-white/40 mt-1">
                  {t('invested', 'invertido')}
                </p>
              </div>

              {/* Status */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-purple-400" />
                  </div>
                  <span className="text-white/60 text-sm">
                    {t('Status', 'Estado')}
                  </span>
                </div>
                <p className="text-xl font-bold">
                  {progress?.status === 'certified' ? t('Certified', 'Certificado') :
                   progress?.status === 'completed' ? t('Completed', 'Completado') :
                   progress?.status === 'in_progress' ? t('In Progress', 'En Progreso') :
                   t('Starting', 'Iniciando')}
                </p>
                <p className="text-xs text-white/40 mt-1">
                  {totalLessonsCompleted}/{totalLessons} {t('lessons', 'lecciones')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        
        {/* Next Step Card - For trainees not yet completed */}
        {!isAdmin && progress?.overall_progress < 100 && currentModule && (
          <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white" data-testid="next-step-card">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <CardContent className="relative p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Sparkles className="h-8 w-8" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <p className="text-blue-100 text-sm font-medium mb-1">
                    {t('Your Next Step', 'Tu Siguiente Paso')}
                  </p>
                  <h3 className="text-xl md:text-2xl font-bold mb-2">
                    {t('Module', 'Módulo')} {currentModule.module_number}: {lang === 'es' ? currentModule.title_es : currentModule.title_en}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-blue-100 text-sm">
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-4 w-4" />
                      {currentModule.lessons_count} {t('lessons', 'lecciones')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {currentModule.estimated_hours}h
                    </span>
                    {(currentModule.user_progress?.completion_percentage || 0) > 0 && (
                      <span className="flex items-center gap-1">
                        <Target className="h-4 w-4" />
                        {currentModule.user_progress.completion_percentage.toFixed(0)}% {t('done', 'completado')}
                      </span>
                    )}
                  </div>
                </div>

                <Button 
                  size="lg"
                  onClick={() => navigate(`/training/module/${currentModule.id}`)}
                  className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg"
                  data-testid="continue-training-btn"
                >
                  {t('Continue', 'Continuar')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Certification Celebration */}
        {progress?.certification && (
          <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white" data-testid="certification-card">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMSIgZmlsbD0id2hpdGUiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+')] opacity-50"></div>
            
            <CardContent className="relative p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <Award className="h-10 w-10" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                {t('Congratulations!', '¡Felicitaciones!')} 🎉
              </h2>
              <p className="text-emerald-100 mb-4">
                {t('You are certified to work in production', 'Estás certificado para trabajar en producción')}
              </p>
              <div className="inline-block bg-white/10 backdrop-blur rounded-xl px-6 py-3">
                <p className="text-xs text-emerald-200 mb-1">
                  {t('Certificate Number', 'Número de Certificado')}
                </p>
                <p className="text-lg font-mono font-bold">
                  {progress.certification.certificate_number}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Modules Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-slate-600" />
              {t('Training Modules', 'Módulos de Capacitación')}
            </h2>
            <Badge variant="outline" className="text-slate-600">
              {completedModules}/{modules.length} {t('completed', 'completados')}
            </Badge>
          </div>

          <div className="grid gap-4">
            {modules.map((module, index) => {
              const status = getModuleStatus(module);
              const modProgress = module.user_progress || {};
              // All modules are unlocked for everyone — sequential gating removed per request
              const isAvailable = true;

              return (
                <Card 
                  key={module.id}
                  data-testid={`module-card-${module.module_number}`}
                  className={`group transition-all duration-200 cursor-pointer ${
                    status === 'completed' 
                      ? 'bg-emerald-50/50 border-emerald-200 hover:border-emerald-300' 
                      : status === 'in_progress' 
                        ? 'bg-blue-50/50 border-blue-200 hover:border-blue-300 shadow-sm' 
                        : isAvailable 
                          ? 'hover:border-slate-300 hover:shadow-sm' 
                          : 'opacity-60 bg-slate-50'
                  }`}
                  onClick={() => (isAvailable || status !== 'locked') && navigate(`/training/module/${module.id}`)}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center gap-4">
                      {/* Module Number Badge */}
                      <div className={`
                        flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center text-lg font-bold transition-transform group-hover:scale-105
                        ${status === 'completed' 
                          ? 'bg-emerald-500 text-white shadow-emerald-200 shadow-lg' 
                          : status === 'in_progress' 
                            ? 'bg-blue-500 text-white shadow-blue-200 shadow-lg' 
                            : isAvailable 
                              ? 'bg-slate-100 text-slate-600' 
                              : 'bg-slate-100 text-slate-400'}
                      `}>
                        {status === 'completed' ? (
                          <CheckCircle className="h-6 w-6" />
                        ) : (
                          module.module_number
                        )}
                      </div>

                      {/* Module Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {lang === 'es' ? module.title_es : module.title_en}
                          </h3>
                          {status === 'in_progress' && (
                            <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                              {t('In Progress', 'En Progreso')}
                            </Badge>
                          )}
                          {status === 'completed' && (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {t('Completed', 'Completado')}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1 hidden md:block">
                          {lang === 'es' ? module.description_es : module.description_en}
                        </p>
                        
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <BookOpen className="h-3.5 w-3.5" />
                            {module.lessons_count} {t('lessons', 'lecciones')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {module.estimated_hours}h
                          </span>
                          {modProgress.completion_percentage > 0 && modProgress.completion_percentage < 100 && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <Target className="h-3.5 w-3.5" />
                              {modProgress.completion_percentage.toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="flex-shrink-0">
                        {(isAvailable || status !== 'locked') ? (
                          <Button
                            variant={status === 'completed' ? 'outline' : 'default'}
                            size="sm"
                            className={status === 'in_progress' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/training/module/${module.id}`);
                            }}
                          >
                            {status === 'completed' ? (
                              t('Review', 'Revisar')
                            ) : status === 'in_progress' ? (
                              <>{t('Continue', 'Continuar')} <ChevronRight className="ml-1 h-4 w-4" /></>
                            ) : (
                              <>{t('Start', 'Iniciar')} <PlayCircle className="ml-1 h-4 w-4" /></>
                            )}
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" disabled className="text-slate-400">
                            <Lock className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {(modProgress.completion_percentage || 0) > 0 && (modProgress.completion_percentage || 0) < 100 && (
                      <Progress 
                        value={modProgress.completion_percentage} 
                        className="mt-4 h-1"
                      />
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Final Exam Card - Open to everyone (sequential gating removed per request) */}
            {(() => {
              const canTakeExam = true;
              const alreadyCertified = !!progress?.certification;
              return (
            <Card 
              data-testid="final-exam-card"
              className={`transition-all duration-200 ${
                alreadyCertified
                  ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'
                  : canTakeExam
                    ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 hover:border-amber-300 cursor-pointer shadow-sm'
                    : 'opacity-60 bg-slate-50'
              }`}
              onClick={() => canTakeExam && navigate('/training/final-exam')}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center ${
                    alreadyCertified
                      ? 'bg-emerald-500 text-white shadow-emerald-200 shadow-lg'
                      : canTakeExam
                        ? 'bg-amber-500 text-white shadow-amber-200 shadow-lg'
                        : 'bg-slate-200 text-slate-400'
                  }`}>
                    {alreadyCertified ? <Award className="h-6 w-6" /> : <Trophy className="h-6 w-6" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">
                        {t('Final Certification Exam', 'Examen Final de Certificación')}
                      </h3>
                      {alreadyCertified && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                          <Award className="h-3 w-3 mr-1" />
                          {t('Certified', 'Certificado')}
                        </Badge>
                      )}
                      {!canTakeExam && !alreadyCertified && (
                        <Badge className="bg-slate-100 text-slate-600 border-0 text-xs">
                          {t('Complete all modules first', 'Completa todos los módulos primero')}
                        </Badge>
                      )}
                      {isAdmin && (
                        <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">
                          {t('Admin Preview', 'Vista Admin')}
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-500 mt-1">
                      {alreadyCertified
                        ? t('You are certified. You can review your exam.', 'Estás certificado. Puedes revisar tu examen.')
                        : canTakeExam
                          ? t('Take the final exam to earn your certification', 'Toma el examen final para obtener tu certificación')
                          : t('Complete all training modules to unlock', 'Completa todos los módulos para desbloquear')}
                    </p>
                    
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <BookOpen className="h-3.5 w-3.5" />
                        150 {t('questions', 'preguntas')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        3h
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="h-3.5 w-3.5" />
                        80% min
                      </span>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    {canTakeExam ? (
                      <Button
                        size="sm"
                        className={alreadyCertified ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-600'}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/training/final-exam');
                        }}
                        data-testid="take-final-exam-btn"
                      >
                        <Trophy className="h-4 w-4 mr-1" />
                        {alreadyCertified ? t('Review', 'Revisar') : isAdmin ? t('Preview', 'Ver') : t('Take Exam', 'Tomar Examen')}
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" disabled className="text-slate-400">
                        <Lock className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
              );
            })()}

            {/* Sandbox Card - Open to all users (sequential gating removed per request) */}
            <Card 
              data-testid="sandbox-card"
              className="transition-all duration-200 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 hover:border-purple-300 cursor-pointer shadow-sm"
              onClick={() => navigate('/training/sandbox')}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center bg-purple-500 text-white shadow-purple-200 shadow-lg">
                    <Play className="h-6 w-6" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">
                        {t('Sandbox Mode', 'Modo Sandbox')}
                      </h3>
                      <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">
                        {isAdmin ? t('Admin', 'Admin') : t('Unlocked', 'Desbloqueado')}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-slate-500 mt-1">
                      {isAdmin 
                        ? t('Preview simulations and tools', 'Vista previa de simulaciones') 
                        : t('Practice in a safe environment', 'Practica en un ambiente seguro')}
                    </p>
                    
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Play className="h-3.5 w-3.5" />
                        11 {t('simulations', 'simulaciones')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Settings className="h-3.5 w-3.5" />
                        {t('Practice tools', 'Herramientas')}
                      </span>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <Button
                      size="sm"
                      className="bg-purple-500 hover:bg-purple-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/training/sandbox');
                      }}
                      data-testid="sandbox-mode-btn"
                    >
                      <Play className="h-4 w-4 mr-1" />
                      {t('Enter', 'Entrar')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
